import React, { useState, useCallback, useEffect, useMemo } from "react";
import { TextField, Autocomplete, CircularProgress, Checkbox } from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import { InputShell } from "./FormInputShell";
import debounce from "lodash.debounce";

// Sentinel option used to render the "Select All" row in multi-select mode
const SELECT_ALL_ID = "__SELECT_ALL__";

interface SearchableInputProps {
  name?: any;
  selected?: any;
  options: any;
  label?: any;
  placeholder?: any;
  onInputChange?: any;
  onOptionSelect?: any;
  customStyle?: any;
  loading?: any;
  fetchMore?: any;
  hasMore?: any;
  isMultiSelect?: boolean;
  maxSelections?: number;
  onMaxSelectionReached?: () => void;
  enableSelectAll?: boolean;
  selectAllLabel?: string;
  [key: string]: any;
}

const SearchableInput = ({
  name,
  selected,
  options,
  label,
  placeholder,
  onInputChange,
  onOptionSelect,
  customStyle,
  loading,
  fetchMore,
  hasMore,
  isMultiSelect = false,
  maxSelections,
  onMaxSelectionReached,
  enableSelectAll = false,
  selectAllLabel = "Select All",
  ...props
}: SearchableInputProps) => {
  const [inputValue, setInputValue] = useState(selected?.label ?? "");
  const [selectedOption, setSelectedOption] = useState(selected ?? null);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  // Keep local selected state in sync with controlled parent state.
  useEffect(() => {
    if (isMultiSelect) {
      setSelectedOption(selected || []);
      return;
    }
    setSelectedOption(selected ?? null);
    // Also sync the visible text so the field isn't blank when options
    // load asynchronously after the component has already mounted.
    setInputValue(selected?.label ?? "");
  }, [selected, isMultiSelect]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedInputChange = useCallback(
    debounce((newInputValue) => {
      if (onInputChange) {
        onInputChange(newInputValue);
      }
    }, 300),
    [onInputChange]
  );

  const realOptions = options || [];
  const selectedCount = Array.isArray(selectedOption) ? selectedOption.length : 0;
  const allSelected =
    isMultiSelect && realOptions.length > 0 && selectedCount === realOptions.length;
  const someSelected = isMultiSelect && selectedCount > 0 && !allSelected;

  // Prepend a synthetic "Select All" row only when explicitly enabled for a multi-select
  const showSelectAll =
    isMultiSelect && enableSelectAll && realOptions.length > 0;
  const displayOptions = useMemo(
    () =>
      showSelectAll
        ? [{ id: SELECT_ALL_ID, label: selectAllLabel }, ...realOptions]
        : realOptions,
    [showSelectAll, selectAllLabel, realOptions]
  );

  const handleInputChange = (_, newInputValue, reason) => {
    if (reason === "input") {
      setInputValue(newInputValue);
      debouncedInputChange(newInputValue);
    } else if (reason === "reset" || reason === "clear") {
      setInputValue(newInputValue);
    }
  };

  const handleOptionChange = (e, newValue, reason) => {
    // "Select All" toggle — selects every available option, or clears when all are already selected
    if (
      showSelectAll &&
      Array.isArray(newValue) &&
      newValue.some((v) => v?.id === SELECT_ALL_ID)
    ) {
      const next = allSelected ? [] : [...realOptions];
      setSelectedOption(next);
      setInputValue("");
      if (onOptionSelect) onOptionSelect({ target: { name, value: next } });
      return;
    }
    if (
      isMultiSelect &&
      Number.isInteger(maxSelections) &&
      (maxSelections as number) > 0 &&
      Array.isArray(newValue) &&
      newValue.length > (maxSelections as number)
    ) {
      if (onMaxSelectionReached) onMaxSelectionReached();
      return;
    }

    setSelectedOption(newValue);

    if (reason !== "clear") {
      setInputValue(
        !isMultiSelect && newValue && typeof newValue === "object"
          ? newValue.label || ""
          : ""
      );
    } else {
      setInputValue("");
    }

    if (onOptionSelect) {
      onOptionSelect({ target: { name, value: newValue } });
    }
  };

  const handleScroll = (event: any) => {
    const listboxNode = event.currentTarget;
    const bottomReached =
      Math.round(listboxNode.scrollHeight - listboxNode.scrollTop) ===
      listboxNode.clientHeight;

    if (bottomReached && hasMore && !isFetchingMore) {
      fetchMore().finally(() => {
        setIsFetchingMore(false);
      });
    }
  };

  const isOptionEqualToValue = (option, value) => {
    if (option.id && value.id) {
      return option.id === value.id;
    }
    return option.label === value.label;
  };

  // Always resolve a string label for chips / input so an option object is never rendered as a child
  const getOptionLabel = (option: any) =>
    option && typeof option === "object" ? option.label ?? "" : option ?? "";

  return (
    <Autocomplete
      multiple={isMultiSelect}
      size={props.size ?? "small"}
      value={selectedOption}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleOptionChange}
      options={displayOptions}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      filterOptions={(options, state) =>
        options.filter((option) =>
          option.label?.toLowerCase().includes(state.inputValue?.toLowerCase())
        )
      }
      renderOption={(props, option, { selected: isSelected }) => {
        const { key, ...otherProps } = props as any;
        if (isMultiSelect) {
          const isSelectAllRow = option.id === SELECT_ALL_ID;
          const checked = isSelectAllRow ? allSelected : isSelected;
          return (
            <li key={option.id || key} {...otherProps}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                indeterminateIcon={
                  <IndeterminateCheckBoxIcon fontSize="small" />
                }
                style={{ marginRight: 8 }}
                checked={checked}
                indeterminate={isSelectAllRow && someSelected}
              />
              {option.label}
            </li>
          );
        }
        return (
          <li key={option.id || key} {...otherProps}>
            {option.label}
          </li>
        );
      }}
      noOptionsText="No data Found"
      ListboxProps={{ onScroll: handleScroll }}
      loading={loading || isFetchingMore}
      renderInput={(params) => (
        <InputShell label={label} name={name}>
          <TextField
            {...params}
            placeholder={placeholder}
            variant="outlined"
            style={customStyle}
            InputLabelProps={{ style: { fontSize: 14 } }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading || isFetchingMore ? (
                    <CircularProgress color="inherit" size={18} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        </InputShell>
      )}
      {...props}
    />
  );
};

export default React.memo(SearchableInput);
